// ── Standalone task detail page (C1167) ──
// Deep-linkable counterpart to the slide-out panel in components/task-detail.js.
// Reuses that module's renderer + every edit handler via mountTaskDetail() —
// see that file's "Two Surfaces, One Renderer" notes for how ownership works.

import { checkAuth, getUser } from '../auth.js';
import { api } from '../api.js';
import { escapeHtml } from '../utils.js';
import { initLayout, getContentEl, updateLayoutContext } from '../layout.js';
import { mountTaskDetail } from '../components/task-detail.js';
import { getNavigationId } from '../router.js';

let state = { projectId: null, taskKey: null, unmount: null };

function resetState() {
  state = { projectId: null, taskKey: null, unmount: null };
}

export async function taskDetailPage(params) {
  // Read before the first await: once it changes, the user navigated on and this mount stops
  // after its next await (see router.js getNavigationId()).
  const navId = getNavigationId();
  const superseded = () => getNavigationId() !== navId;
  if (!(await checkAuth()) || superseded()) return;

  resetState();
  const owner = state;
  const projectId = params.id;
  const taskKey = decodeURIComponent(params.taskKey);
  state.projectId = projectId;
  state.taskKey = taskKey;

  initLayout(getUser());
  updateLayoutContext({ projectId, projectName: null, activeView: 'board', viewLabel: taskKey });

  const content = getContentEl();
  content.innerHTML = `
    <div class="page-header task-detail-page-header">
      <a class="btn btn-ghost btn-sm" href="#/projects/${projectId}/board">&larr; Board</a>
      <h2>${escapeHtml(taskKey)}</h2>
    </div>
    <div class="task-detail-page" id="task-detail-host">
      <div class="task-detail-body"><div class="empty"><span class="spinner"></span></div></div>
    </div>`;
  // .task-detail-page here styles the first (pre-mount) paint above; mountTaskDetail() also
  // asserts the same class on this host so the page-mode CSS hook doesn't depend on this
  // markup alone (see components/task-detail.js).

  const host = content.querySelector('#task-detail-host');

  // Targeted prefetch (TPT618) — never the project's task list. The page holds only the
  // task's children (GET /tasks/:taskKey/children, full rows incl. story_points) as
  // options.allTasks, and the Sprint field's in-use numbers as `{ priority }` rows from
  // GET /tasks/facets `priorities` — the same stand-in board.js's facetSprintRows() hands the
  // New Task modal. mountTaskDetail() resolves the parent itself from the task's parent_key,
  // tag suggestions come from the tag registry, and Change Parent searches the server.
  // Sequential (not parallel with the detail fetch) — avoids a two-phase flash where those
  // sections pop in late. Each leg degrades softly: the task itself still loads, the children
  // list renders empty, and without facets the sprint numbers fall back to the children's.
  const tasksPath = '/projects/' + projectId + '/tasks';
  const [childrenRes, facetsRes] = await Promise.all([
    api.get(tasksPath + '/' + encodeURIComponent(taskKey) + '/children?include_children_count=false').catch(() => null),
    api.get(tasksPath + '/facets').catch(() => null),
  ]);
  if (superseded()) return;
  const options = {
    allTasks: (childrenRes && childrenRes.children) || [],
    sprintRows: facetsRes && Array.isArray(facetsRes.priorities)
      ? facetsRes.priorities.map(priority => ({ priority }))
      : null,
  };

  try {
    const unmount = await mountTaskDetail(host, projectId, taskKey, options);
    // Superseded during the mount: mountTaskDetail() resolves null when it saw the navigation
    // itself (it released its own bindings); an unmount fn still gets released at once here,
    // nothing stores it.
    if (superseded()) {
      if (unmount) unmount();
      return;
    }
    state.unmount = unmount;
  } catch (err) {
    if (superseded()) return;
    host.innerHTML = `<div class="empty text-muted">
      ${escapeHtml(err.message || 'Failed to load task')}
      <div class="mt-1"><a href="#/projects/${projectId}/board">Back to board</a></div>
    </div>`;
  }

  return () => {
    // A late teardown (router.js runs it as soon as it resolves) must leave a newer mount of
    // this page alone: that mount owns the module state by then.
    if (state !== owner) return;
    // The unmount closes the modal the detail view opened (TPT647); this page opens no other
    // modal and no assignee picker, so it closes nothing it does not own.
    if (state.unmount) state.unmount();
    resetState();
  };
}
